import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { Image, Dropdown, Button, Tag, Table, Tabs, Modal, message, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SeoHead } from 'modules/seo';
import { Divider } from 'modules/ui';
import { DocumentData } from '../types';
import { DocumentProcessingDetailPage } from './DocumentProcessingDetailPage';
import { useDocumentProcessing } from './DocumentProcessingContext';
import { TableSearch } from 'modules/table';
import { DatePicker, Input, Select } from 'modules/form';
import more from 'assets/images/more.svg';
import { useFelaEnhanced } from 'hooks';
import * as felaRules from './DocumentProcessingPage.styles';
import { ONBOARDING_LINK_COLOR, ONBOARDING_TEXT_COLOR } from './DocumentProcessingPage.constants';
import { DocumentUploadModal } from './DocumentUploadModal';
import DocumentTypeChange from './DocumentTypeChange';
import PDFSplitter from './PDFSplitter';
import PDFViewer from './PDFViewer';
import LandingPageDocumentsTab from './LandingPageDocumentsTab';
import { parseDate } from '../utils';
import { UnclassifiedDocument, OnboardingDocument } from '../types'

const menuOptions = [
  {
    label: <FormattedMessage id="document.processing.action.view" />,
    id: 'view',
  },
  {
    label: <FormattedMessage id="document.processing.action.split" />,
    id: 'split',
  },
  {
    label: <FormattedMessage id="document.processing.action.delete" />,
    id: 'delete',
  },
  {
    label: <FormattedMessage id="document.processing.action.change.type" />,
    id: 'changeType',
  },
  {
    label: <FormattedMessage id="document.processing.action.resend.for.extraction" />,
    id: 'resendForExtraction',
  },
];

const aiSubmittedMenu = [
  {
    label: <FormattedMessage id="document.processing.action.view" />,
    id: 'view',
  },
  {
    label: <FormattedMessage id="document.processing.action.submit.to.pending" />,
    id: 'submitToPending',
  },
  {
    label: <FormattedMessage id="document.processing.action.resend.for.extraction" />,
    id: 'resendForExtraction',
  },
];

const completedMenu = [
  {
    label: <FormattedMessage id="document.processing.action.view" />,
    id: 'view',
  },
  {
    label: <FormattedMessage id="document.processing.action.revert.to.pending" />,
    id: 'revertToPending',
  }
];

const deletedMenu = [
  {
    label: <FormattedMessage id="document.processing.action.revert.to.pending" />,
    id: 'restoreDeletedToPending',
  },
];

const validationMenu = menuOptions.filter(option => option.id !== 'split');

const getGStoreId = (record: DocumentData): string =>
  String(record.gstoreId ?? record.jsonContent?.GStoreId ?? '').trim();

const DocumentProcessingPage: React.FC = () => {
  const { styles } = useFelaEnhanced(felaRules);
  const [openDocumentTypeChange, setOpenDocumentTypeChange] = React.useState(false);
  const [showSplitModal, setShowSplitModal] = React.useState(false);
  const [splitFilename, setSplitFilename] = React.useState('');
  const [showUnclassifiedPdfModal, setShowUnclassifiedPdfModal] = React.useState(false);
  const [unclassifiedSelectedName, setUnclassifiedSelectedName] = React.useState('');
  const [showOnboardingPdfModal, setShowOnboardingPdfModal] = React.useState(false);
  const [onboardingSelectedName, setOnboardingSelectedName] = React.useState('');
  const [isOnboarding, setIsOnboarding] = React.useState(false);
  const intl = useIntl();
  const {
    loading,
    selectedDocument,
    extractedData,
    getPdfUrl,
    pdfDetails,
    closeModal,
    filteredTableData,
    filterOptions,
    setFilterOptions,
    tabKey,
    setTabKey,
    detailModalVisible,
    setDetailModalVisible,
    handleDeleteDocument,
    setSelectedDocument,
    showDeleteModal,
    setShowDeleteModal,
    revertToPending,
    submitAiSubmittedToPending,
    showRevertModal,
    setShowRevertModal,
    setShowDocumentUploadModal,
    fetchDocuments,
    getDocumentTypeBaseOnTabKey,
    pagination,
    setPagination,
    unclassifiedDocuments,
    fetchUnclassifiedDocuments,
    viewUnclassifiedDocument,
    unclassifiedPdfUrl,
    setUnclassifiedPdfUrl,
    classifyUnclassifiedDocument,
    deleteUnclassifiedDocument,
    restoreDeletedToPending,
    resendForExtraction,
    showReextractModal,
    setShowReextractModal,
    isSubmitting,
    showDuplicateModal,
    setShowDuplicateModal,
    duplicateDocument,
    onboardingDocuments,
    fetchOnboardingDocuments,
    viewOnboardingDocument,
    classifyOnboardingDocument,
    onboardingPdfUrl,
    setOnboardingPdfUrl,
  } = useDocumentProcessing();


  useEffect(() => {
    // Find the iframe by its style attributes
    const iframe = document.querySelector('iframe[style*="position: fixed"][style*="z-index: 2147483647"]');
    
    if (iframe) {
      iframe.remove();
    }
    
    // Cleanup function to ensure removal
    return () => {
      const iframe = document.querySelector('iframe[style*="position: fixed"][style*="z-index: 2147483647"]');
      if (iframe) {
        iframe.remove();
      }
    };
  }, []);

  // const getStatusColor = (status: string) => {
  //   switch (status) {
  //     case 'pending':
  //       return 'orange';
  //     case 'processing':
  //       return 'blue';
  //     case 'completed':
  //       return 'green';
  //     case 'error':
  //       return 'red';
  //     default:
  //       return 'default';
  //   }
  // };

  const typeLabels = [
    {
      label:intl.formatMessage({ id: 'document.processing.type.invoice' }),
      value: 'invoice'
    },
    {
      label: intl.formatMessage({ id: 'document.processing.type.assessment_report' }),
      value: 'assessment report'
    },
    {
      label: intl.formatMessage({ id: 'document.processing.type.contract' }),
      value: 'contract'
    },
    {
      label: intl.formatMessage({ id: 'document.processing.type.document' }),
      value: 'document'
    },
    {
      label: intl.formatMessage({ id: 'document.processing.type.quotation' }),
      value: 'quotation'
    },
    {
      label: intl.formatMessage({ id: 'document.processing.type.price_adjustment' }),
      value: 'price adjustment'
    },
  ];

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'Customer_Invoice_Stage':
      case 'Customer_Invoice_Prod':
        return intl.formatMessage({ id: 'document.processing.type.invoice' });
      case 'Assessment_Report_Prod':
      case 'Assessment_Report_Stage':
        return intl.formatMessage({ id: 'document.processing.type.assessment_report' });
      case 'Customer_Contract_Prod':
        return intl.formatMessage({ id: 'document.processing.type.contract' });
      case 'Customer_Document_Prod':
        return intl.formatMessage({ id: 'document.processing.type.document' });
      case 'Customer_Quotation_Prod':
        return intl.formatMessage({ id: 'document.processing.type.quotation' });
      case 'Price_Adjustment_Prod':
        return intl.formatMessage({ id: 'document.processing.type.price_adjustment' });
      case 'Landing_Page_Assessment_Report':
        return intl.formatMessage({ id: 'document.processing.type.landing_page_assessment_report' });
      default:
        return type;
    }
  };


  const handleViewDocument = async (record: DocumentData) => {
      try {
        await getPdfUrl(record);
        setDetailModalVisible(true);
      } catch (error: any) {
        console.error('Error fetching document details:', error);
        message.error(
          intl.formatMessage({
            id: 'document.processing.error.pdf.not.found',
            defaultMessage: 'PDF file not found or could not be loaded'
          })
        );
      }

  };

  const handleSplitDocument = async (record: DocumentData) => {
    try {
      await getPdfUrl(record);
      setSplitFilename(record.filename);
      setShowSplitModal(true);
    } catch (error: any) {
      message.error(
        intl.formatMessage({
          id: 'document.processing.error.pdf.not.found',
          defaultMessage: 'PDF file not found or could not be loaded'
        })
      );
    }
  };

  const handleClick = (option: any, record: DocumentData) => {
    if (option.id === 'view') {
      handleViewDocument(record);
    } else if (option.id === 'split') {
      handleSplitDocument(record);
    } else if (option.id === 'delete') {
      setSelectedDocument(record);
      setShowDeleteModal(true);
    } else if (option.id === 'revertToPending' || option.id === 'submitToPending') {
      setSelectedDocument(record);
      setShowRevertModal(true);
    } else if (option.id === 'changeType') {
      setSelectedDocument(record);
      setOpenDocumentTypeChange(true);
    } else if (option.id === 'restoreDeletedToPending') {
      setSelectedDocument(record);
      setShowRevertModal(true);
    } else if (option.id === 'resendForExtraction') {
      setSelectedDocument(record);
      setShowReextractModal(true);
    }
  };

  const isOnboardingDoc = (record: DocumentData): boolean => {
    const content = record.jsonContent?.Metadata?.results?.[0]?.content;
    if (!content) return false;
    const emailSection = content.find(s => s.schema_id === 'email_info_section');
    return emailSection?.children?.some(
      c => c.schema_id === 'email_tags' && c.value === 'ONBOARDING'
    ) ?? false;
  };

  const columns: ColumnsType<DocumentData> = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'document.processing.table.filename' }),
        dataIndex: 'filename',
        key: 'filename',
        sorter: (a, b) => a.filename.localeCompare(b.filename),
        width: 150,
        render: (text: string, record: DocumentData) => tabKey !== '3' ? (
            <Button
                style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              height: 'fit-content',
              padding: 0,
              border: 'none',
              background: 'none',
              color: isOnboardingDoc(record) ? ONBOARDING_LINK_COLOR : '#1890ff',
              textAlign: 'left',
            }}
                onClick={() => handleViewDocument(record)}
          >
                {text}
            </Button>
        ) : (
            <div
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  color: isOnboardingDoc(record) ? ONBOARDING_TEXT_COLOR : undefined,
                }}
            >
                {text}
            </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'document.processing.table.gstoreId' }),
        key: 'gstoreId',
        width: 150,
        sorter: (a, b) => getGStoreId(a).localeCompare(getGStoreId(b)),
        render: (_: unknown, record: DocumentData) => (
            <div
                style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
                {getGStoreId(record)}
            </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'service_receiver_name' }),
        dataIndex: 'senderReceiver',
        key: 'senderReceiver',
        onHeaderCell: () => ({
          style: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.2' },
        }),
        sorter: (a, b) => a.senderReceiver.localeCompare(b.senderReceiver),
        width: 150,
        render: (text: string) => (
            <div
                style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
                {text}
            </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'service_receiver_address' }),
        dataIndex: 'senderReceiverAddress',
        key: 'senderReceiverAddress',
        sorter: (a, b) => a.senderReceiverAddress.localeCompare(b.senderReceiverAddress),
        width: 150,
        onHeaderCell: () => ({
          style: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.2' },
        }),
        render: (text: string) => (
            <div
                style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
                {text}
            </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'sender_name' }),
        dataIndex: 'account',
        key: 'account',
        onHeaderCell: () => ({
          style: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.2' },
        }),
        sorter: (a, b) => a.account.localeCompare(b.account),
        width: 100,
        render: (text: string) => (
            <div
                style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
                {text}
            </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'document.processing.table.type' }),
        dataIndex: 'type',
        key: 'type',
        width: 120,
        render: type => (
          <Tag color="blue">
            {getTypeLabel(type)}
          </Tag>
        ),
        filters: [{ text: getTypeLabel('invoice'), value: 'invoice' }],
        onFilter: (value, record) => record.type === value,
      },
      {
        title: intl.formatMessage({ id: 'document.processing.table.fabrikNumber' }),
        dataIndex: 'fabrikNumber',
        key: 'fabrikNumber',
        width: 150,
        render: (text: string) => (
            <div
                style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
                {text}
            </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'document.date' }),
        dataIndex: 'documentDate',
        key: 'documentDate',
        width: 100,
        render: date => date ? parseDate(date)?.toLocaleDateString() : '',
      },
      {
        title: intl.formatMessage({ id: 'document.processing.table.uploadedAt' }),
        dataIndex: 'uploadedAt',
        key: 'uploadedAt',
        width: 100,
        render: date => date ? parseDate(date)?.toLocaleDateString() : '',
        sorter: (a, b) => new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime(),
      },
      {
        title: intl.formatMessage({ id: 'document.processing.table.actions' }),
        key: 'actions',
        width: 50,
        render: (_, record) => {
          const menu =
            tabKey === '7'
              ? aiSubmittedMenu
              : tabKey === '8'
                ? validationMenu
                : tabKey === '1'
                  ? menuOptions
                  : tabKey === '2'
                    ? completedMenu
                    : deletedMenu;
          return (
            <Dropdown
                menu={{
                    items: menu.map(option => ({
                        key: option.id,
                        label: option.label,
                        onClick: () => handleClick(option, record),
                    })),
                }}
            >
                <Button onClick={e => e.preventDefault()} className={styles.moreButton}>
                    <Image src={more} alt="more" preview={false} />
                </Button>
            </Dropdown>
          );
        },
      },
    ],
    [getTypeLabel, handleClick, handleViewDocument, intl, styles.moreButton, tabKey],
  );

  const handleTabChange = key => {
    setFilterOptions({});
    setIsOnboarding(false);
    setPagination({ current: 1, pageSize: 10, total: 0 });
    if (key === '7') {
      setTabKey('7');
      fetchDocuments(getDocumentTypeBaseOnTabKey('7'), {}, 1, 10, false);
    } else if (key === '1') {
      setTabKey('1');
      fetchDocuments(getDocumentTypeBaseOnTabKey('1'), {}, 1, 10, false);
    } else if (key === '8') {
      setTabKey('8');
      fetchDocuments(getDocumentTypeBaseOnTabKey('8'), {}, 1, 10, false);
    } else if (key === '2') {
      setTabKey('2');
      fetchDocuments(getDocumentTypeBaseOnTabKey('2'), {}, 1, 10);
    } else if (key === '3') {
      setTabKey('3');
      fetchDocuments(getDocumentTypeBaseOnTabKey('3'), {}, 1, 10);
    } else if (key === '4') {
      setTabKey('4');
      fetchUnclassifiedDocuments();
    } else if (key === '5') {
      setTabKey('5');
      fetchOnboardingDocuments();
    } else if (key === '6') {
      setTabKey('6');
    }
  };

  const handleFilterChange = (filters: { [key: string]: string }) => {
    setFilterOptions(prev => ({ ...prev, ...filters }));
  };

  const handleSubmit = event => {
    event.preventDefault();
    setPagination({ current: 1, pageSize: pagination.pageSize, total: 0 });
    fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions, 1, pagination.pageSize, isOnboarding);
  };

  const handleReset = () => {
    setFilterOptions({});
    setIsOnboarding(false);
    setPagination({ current: 1, pageSize: pagination.pageSize, total: 0 });
    fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), {}, 1, pagination.pageSize, false);
  };

  const handleTableChange = (newPagination) => {
    const { current, pageSize } = newPagination;
    setPagination({ current, pageSize, total: pagination.total });
    fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions, current, pageSize, isOnboarding);
  };

  const handleViewUnclassifiedDocument = async (name: string) => {
    try {
      await viewUnclassifiedDocument(name);
      setUnclassifiedSelectedName(name);
      setShowUnclassifiedPdfModal(true);
    } catch (error) {
      message.error(
        intl.formatMessage({
          id: 'document.processing.error.pdf.not.found',
          defaultMessage: 'PDF file not found or could not be loaded'
        })
      );
    }
  };

  const handleViewOnboardingDocument = async (name: string) => {
    try {
      await viewOnboardingDocument(name);
      setOnboardingSelectedName(name);
      setShowOnboardingPdfModal(true);
    } catch (error) {
      message.error(
        intl.formatMessage({
          id: 'document.processing.error.pdf.not.found',
          defaultMessage: 'PDF file not found or could not be loaded'
        })
      );
    }
  };

  const onboardingColumns: ColumnsType<OnboardingDocument> = [
    {
      title: intl.formatMessage({ id: 'document.processing.table.filename' }),
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Button
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            height: 'fit-content',
            padding: 0,
            border: 'none',
            background: 'none',
            color: '#1890ff',
            textAlign: 'left',
          }}
          onClick={() => handleViewOnboardingDocument(text)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: intl.formatMessage({ id: 'document.processing.table.uploadedAt' }),
      dataIndex: 'updated',
      key: 'updated',
      width: 150,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '',
      sorter: (a, b) => new Date(a.updated).getTime() - new Date(b.updated).getTime(),
    },
  ];

  const unclassifiedColumns: ColumnsType<UnclassifiedDocument> = [
    {
      title: intl.formatMessage({ id: 'document.processing.table.filename' }),
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Button
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            height: 'fit-content',
            padding: 0,
            border: 'none',
            background: 'none',
            color: '#1890ff',
            textAlign: 'left',
          }}
          onClick={() => handleViewUnclassifiedDocument(text)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: intl.formatMessage({ id: 'document.processing.table.uploadedAt' }),
      dataIndex: 'updated',
      key: 'updated',
      width: 150,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '',
      sorter: (a, b) => new Date(a.updated).getTime() - new Date(b.updated).getTime(),
    },
  ];

  const renderTabSearch = () => (
      <TableSearch
          fields={[
        {
          label: <FormattedMessage id={`document.processing.table.filename`} />,
          labelFor: 'filename',
          renderer: (
              <Input
                  id="filename"
                  data-testid="filename"
                  value={filterOptions['filename']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={({ target }) => {
                  handleFilterChange({ [target.id]: target.value });
              }}
            />
          ),
        },
        {
          label: <FormattedMessage id={`document.processing.table.gstoreId`} />,
          labelFor: 'document.processing.table.gstoreId',
          renderer: (
              <Input
                  id="gstoreId"
                  data-testid="gstoreId"
                  value={filterOptions['gstoreId']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={({ target }) => {
                handleFilterChange({ [target.id]: target.value });
              }}
            />
          ),
        },
        {
          label: <FormattedMessage id={`service_receiver_name`} />,
          labelFor: 'service_receiver_name',
          renderer: (
              <Input
                  id="senderReceiver"
                  data-testid="senderReceiver"
                  value={filterOptions['senderReceiver']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={({ target }) => {
                handleFilterChange({ [target.id]: target.value });
              }}
            />
          ),
        },
         {
          label: <FormattedMessage id={`service_receiver_address`} />,
          labelFor: 'service_receiver_address',
          renderer: (
              <Input
                  id="senderReceiverAddress"
                  data-testid="senderReceiverAddress"
                  value={filterOptions['senderReceiverAddress']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={({ target }) => {
                handleFilterChange({ [target.id]: target.value });
              }}
            />
          ),
        },
        {
          label: <FormattedMessage id={'document.processing.table.uploadedAt'} />,
          labelFor: 'uploadedAt',
          renderer: (
              <DatePicker
                  id="uploadedAt"
                  onChange={(date) => {
                      handleFilterChange({
                          uploadedAt: date ? moment(date).format('YYYY-MM-DD') : null,
                      });
                  }}
                  value={filterOptions['uploadedAt'] ? moment(filterOptions['uploadedAt']) : null}
              />
          ),
      },
          {
          label: <FormattedMessage id={'document.date'} />,
          labelFor: 'documentDate',
          renderer: (
              <DatePicker
                  id="documentDate"
                  onChange={(date) => {
                      handleFilterChange({
                          documentDate: date ? moment(date).format('YYYY-MM-DD') : null,
                      });
                  }}
                  value={filterOptions['documentDate'] ? moment(filterOptions['documentDate']) : null}
              />
          ),
      },
      {
          label: <FormattedMessage id={`sender_name`} />,
          labelFor: 'sender_name',
          renderer: (
              <Input
                  id="account"
                  data-testid="account"
                  value={filterOptions['account']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={({ target }) => {
                handleFilterChange({ [target.id]: target.value });
              }}
            />
          ),
        },
        {
          label: <FormattedMessage id={`document.processing.table.fabrikNumber`} />,
          labelFor: 'document.processing.table.fabrikNumber',
          renderer: (
              <Input
                  id="fabrikNumber"
                  data-testid="fabrikNumber"
                  value={filterOptions['fabrikNumber']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={({ target }) => {
                handleFilterChange({ [target.id]: target.value });
              }}
            />
          ),
        },
        {
          label: <FormattedMessage id={`document.processing.table.type`} />,
          labelFor: 'type',
          renderer: (
              <Select
                  id="type"
                  data-testid="type"
                  value={filterOptions['type']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={value => handleFilterChange({ type: value })}
                  options={typeLabels.map(({label, value}) => ({ label, value }))}
                  fullWidth
                />
          ),
        },
        ...(tabKey === '1' ? [{
          label: null,
          labelFor: 'isOnboarding',
          renderer: (
            <Checkbox
              id="isOnboarding"
              checked={isOnboarding}
              onChange={e => setIsOnboarding(e.target.checked)}
            >
              {intl.formatMessage({ id: 'document.processing.filter.onboarding', defaultMessage: 'Onboarding only' })}
            </Checkbox>
          ),
        }] : []),
      ]}
          handleSubmit={handleSubmit}
          handleReset={handleReset}
          rowGutter={[16, 16]}
          colSizes={{ sm: 24, md: 12, lg: 8, xxl: 3 }}
    />
  );

  return (
      <>
          <div>
              <SeoHead title={intl.formatMessage({ id: 'page.documentProcessing.title' })} />
          </div>
          <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                      <h1>{intl.formatMessage({ id: 'page.documentProcessing.title' })}</h1>
                      <p>{intl.formatMessage({ id: 'page.documentProcessing.description' })}</p>
                  </div>
                  <Button type="primary" size="large" onClick={() => setShowDocumentUploadModal(true)}>
                      <FormattedMessage id="documents.generalUpload.cta" />
                  </Button>
              </div>
              <Divider />
              <Tabs activeKey={tabKey} onChange={handleTabChange} >
                  <Tabs.TabPane tab={<span>{intl.formatMessage({ id: 'guest.documents.gcpStatus.pending' })}</span>} key="1" disabled={loading}>
                      {renderTabSearch()}
                      <Table
                          columns={columns}
                          dataSource={filteredTableData}
                          loading={loading}
                          rowKey={(record) => record.filename + record.uploadedAt}
                          rowClassName={(record) => isOnboardingDoc(record) ? styles.onboardingRow : ''}
                          pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                          }}
                          onChange={handleTableChange}
                          scroll={{ x: 'max-content' }}
                      />
                  </Tabs.TabPane>
                  <Tabs.TabPane tab={<span>{intl.formatMessage({ id: 'document.processing.status.documentInvestigation' })}</span>} key="8" disabled={loading}>
                      {renderTabSearch()}
                      <Table
                          columns={columns}
                          dataSource={filteredTableData}
                          loading={loading}
                          rowKey={(record) => record.filename + record.uploadedAt}
                          rowClassName={(record) => isOnboardingDoc(record) ? styles.onboardingRow : ''}
                          pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                          }}
                          onChange={handleTableChange}
                          scroll={{ x: 'max-content' }}
                      />
                  </Tabs.TabPane>
                  <Tabs.TabPane tab={<span>{
                    intl.formatMessage({ id: 'document.processing.status.unclassified' })}</span>} key="4" disabled={loading}>
                      <Table
                          columns={unclassifiedColumns}
                          dataSource={unclassifiedDocuments}
                          loading={loading}
                          rowKey={(record) => record.name}
                          pagination={false}
                      />
                  </Tabs.TabPane>
                  <Tabs.TabPane tab={<span>{intl.formatMessage({ id: 'document.processing.status.completed' })}</span>} key="2" disabled={loading}>
                      {renderTabSearch()}
                      <Table
                          columns={columns}
                          dataSource={filteredTableData}
                          loading={loading}
                          rowKey={(record) => record.filename + record.uploadedAt}
                          rowClassName={(record) => isOnboardingDoc(record) ? styles.onboardingRow : ''}
                          pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                          }}
                          onChange={handleTableChange}
                          scroll={{ x: 'max-content' }}
                      />
                  </Tabs.TabPane>
                  <Tabs.TabPane tab={<span>{intl.formatMessage({ id: 'document.processing.status.deleted' })}</span>} key="3" disabled={loading}>
                      {renderTabSearch()}
                      <Table
                          columns={columns}
                          dataSource={filteredTableData}
                          loading={loading}
                          rowKey={(record) => record.filename + record.uploadedAt}
                          rowClassName={(record) => isOnboardingDoc(record) ? styles.onboardingRow : ''}
                          pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                          }}
                          onChange={handleTableChange}
                      />
                  </Tabs.TabPane>
                  <Tabs.TabPane tab={<span>{intl.formatMessage({ id: 'document.processing.status.onboarding' })}</span>} key="5" disabled={loading}>
                      <Table
                          columns={onboardingColumns}
                          dataSource={onboardingDocuments}
                          loading={loading}
                          rowKey={(record) => record.name}
                          pagination={false}
                      />
                  </Tabs.TabPane>
                  <Tabs.TabPane tab={<span>{intl.formatMessage({ id: 'document.processing.tab.landingPageDocuments' })}</span>} key="6">
                      {tabKey === '6' ? <LandingPageDocumentsTab isActive /> : null}
                  </Tabs.TabPane>
                  <Tabs.TabPane tab={<span>{intl.formatMessage({ id: 'document.processing.status.aiSubmitted' })}</span>} key="7" disabled={loading}>
                      {renderTabSearch()}
                      <Table
                          columns={columns}
                          dataSource={filteredTableData}
                          loading={loading}
                          rowKey={(record) => record.filename + record.uploadedAt}
                          rowClassName={(record) => isOnboardingDoc(record) ? styles.onboardingRow : ''}
                          pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                          }}
                          onChange={handleTableChange}
                          scroll={{ x: 'max-content' }}
                      />
                  </Tabs.TabPane>
              </Tabs>
          </div>

          {selectedDocument && (
              <DocumentProcessingDetailPage
                  document={{
                ...selectedDocument,
                extractedData: extractedData,
              }}
                  title={selectedDocument.filename}
                  isOpen={detailModalVisible}
                  closeModal={closeModal}
            />
          )}
          <Modal
              title={intl.formatMessage({ id: 'document.processing.action.delete.document' })}
              open={showDeleteModal}
              onOk={
                () => {
                  handleDeleteDocument(selectedDocument)
                }
              }
              onCancel={() => setShowDeleteModal(false)}
      >
              <p>{intl.formatMessage({ id: 'document.processing.delete.confirmation' })}</p>
          </Modal>
          <Modal
              title={intl.formatMessage({
                id: tabKey === '7'
                  ? 'document.processing.action.submit.to.pending'
                  : 'document.processing.action.revert',
              })}
              open={showRevertModal}
              onOk={
                () => {
                  if (tabKey === '3') {
                    restoreDeletedToPending(selectedDocument);
                  } else if (tabKey === '7') {
                    submitAiSubmittedToPending(selectedDocument);
                  } else {
                    revertToPending(selectedDocument);
                  }
                }
              }
              onCancel={() => setShowRevertModal(false)}
      >
              <p>{intl.formatMessage({
                id: tabKey === '7'
                  ? 'document.processing.submit.to.pending.confirmation'
                  : 'document.processing.revert.confirmation',
              })}</p>
          </Modal>
          <Modal
              title={intl.formatMessage({ id: 'document.processing.action.resend.for.extraction' })}
              open={showReextractModal}
              confirmLoading={isSubmitting}
              onOk={() => resendForExtraction(selectedDocument)}
              onCancel={() => setShowReextractModal(false)}
      >
              <p>{intl.formatMessage({ id: 'document.processing.reextract.confirmation' })}</p>
          </Modal>
          <Modal
              title={intl.formatMessage({ id: 'document.processing.duplicate.modal.title' })}
              open={showDuplicateModal}
              onCancel={() => setShowDuplicateModal(false)}
              footer={[
                <Button key="cancel" onClick={() => setShowDuplicateModal(false)}>
                  {intl.formatMessage({ id: 'document.processing.duplicate.modal.cancel' })}
                </Button>,
                <Button
                  key="delete"
                  type="primary"
                  danger
                  onClick={async () => {
                    await handleDeleteDocument(duplicateDocument);
                    setShowDuplicateModal(false);
                  }}
                >
                  {intl.formatMessage({ id: 'document.processing.duplicate.modal.delete' })}
                </Button>,
              ]}
          >
              <p>{intl.formatMessage({ id: 'document.processing.duplicate.modal.content' })}</p>
          </Modal>
          <DocumentUploadModal />
          <DocumentTypeChange open={openDocumentTypeChange} onClose={() => setOpenDocumentTypeChange(false)}/>
          <Modal
              title={intl.formatMessage({ id: 'document.processing.split.title' })}
              open={showSplitModal}
              onCancel={() => {
                setShowSplitModal(false);
                setSplitFilename('');
              }}
              footer={null}
              width={500}
          >
              <PDFSplitter pdfUrl={pdfDetails?.url || null} filename={splitFilename} />
          </Modal>
          <Modal
              title={unclassifiedSelectedName}
              open={showUnclassifiedPdfModal}
              onCancel={() => {
                setShowUnclassifiedPdfModal(false);
                setUnclassifiedSelectedName('');
                setUnclassifiedPdfUrl(null);
              }}
              footer={null}
              width="90%"
              bodyStyle={{ padding: 0 }}
              destroyOnClose
          >
              {unclassifiedPdfUrl && (
                  <div style={{ display: 'flex', gap: 0 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                          <PDFViewer
                              onDelete={async () => {
                                try {
                                  await deleteUnclassifiedDocument(unclassifiedSelectedName);
                                  setShowUnclassifiedPdfModal(false);
                                  setUnclassifiedSelectedName('');
                                } catch (error) {
                                  message.error(
                                    intl.formatMessage({
                                      id: 'document.processing.error.delete.failed',
                                      defaultMessage: 'Failed to delete document'
                                    })
                                  );
                                }
                              }}
                              pdfUrl={unclassifiedPdfUrl}
                              filename={unclassifiedSelectedName}
                              hideControls
                              documentTypeOptions={typeLabels}
                              classifyLoading={isSubmitting}
                              onClassify={async (documentType) => {
                                try {
                                  await classifyUnclassifiedDocument(unclassifiedSelectedName, documentType);
                                  setShowUnclassifiedPdfModal(false);
                                  setUnclassifiedSelectedName('');
                                } catch (error) {
                                  message.error(
                                    intl.formatMessage({
                                      id: 'document.processing.error.classify.failed',
                                      defaultMessage: 'Failed to classify document'
                                    })
                                  );
                                }
                              }}
                          />
                      </div>
                      <div style={{ width: 460, borderLeft: '1px solid #f0f0f0', overflowY: 'auto', maxHeight: '70vh' }}>
                          <PDFSplitter
                              pdfUrl={unclassifiedPdfUrl}
                              filename={unclassifiedSelectedName}
                              collection="unclassified"
                              aiOnly
                              onSuccess={async () => {
                                setShowUnclassifiedPdfModal(false);
                                setUnclassifiedSelectedName('');
                                setUnclassifiedPdfUrl(null);
                                await fetchUnclassifiedDocuments();
                              }}
                          />
                      </div>
                  </div>
              )}
          </Modal>
          <Modal
              title={onboardingSelectedName}
              open={showOnboardingPdfModal}
              onCancel={() => {
                setShowOnboardingPdfModal(false);
                setOnboardingSelectedName('');
                setOnboardingPdfUrl(null);
              }}
              footer={null}
              width="90%"
              bodyStyle={{ padding: 0 }}
              destroyOnClose
          >
              {onboardingPdfUrl && (
                  <div style={{ display: 'flex', gap: 0 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                          <PDFViewer
                              pdfUrl={onboardingPdfUrl}
                              filename={onboardingSelectedName}
                              hideControls
                              documentTypeOptions={typeLabels}
                              classifyLoading={isSubmitting}
                              onClassify={async (documentType) => {
                                try {
                                  await classifyOnboardingDocument(onboardingSelectedName, documentType);
                                  setShowOnboardingPdfModal(false);
                                  setOnboardingSelectedName('');
                                } catch (error) {
                                  message.error(
                                    intl.formatMessage({
                                      id: 'document.processing.error.classify.failed',
                                      defaultMessage: 'Failed to classify document'
                                    })
                                  );
                                }
                              }}
                          />
                      </div>
                      <div style={{ width: 460, borderLeft: '1px solid #f0f0f0', overflowY: 'auto', maxHeight: '70vh' }}>
                          <PDFSplitter
                              pdfUrl={onboardingPdfUrl}
                              filename={onboardingSelectedName}
                              collection="onboarding"
                              aiOnly
                              onSuccess={async () => {
                                setShowOnboardingPdfModal(false);
                                setOnboardingSelectedName('');
                                setOnboardingPdfUrl(null);
                                await fetchOnboardingDocuments();
                              }}
                          />
                      </div>
                  </div>
              )}
          </Modal>
      </>
  );
};

export { DocumentProcessingPage };
